import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import ControlledTab from "./utils/ControlledTab";
import {TabsContext} from "./tabs.context";

import "./utils/tabs.scss";

//* Tab structure example:
//* {TabComponent: ReqRegisterWarehouseTab,controlled: true, title: "Requests register Warehouse", isNotClosable: true}

//* add additionalTabs to withTabs({})(FirstTab, [here]) to open several tabs at once
//* Example:
//   export default withTabs({
//   isMainTabControlled: true,
//   isClosableTabs: true,
//   label: "Requests in progress",
//   formInitialValues: {},
// })(RequestsInProgress, [
//   {TabComponent: ReqRegisterWarehouseTab, controlled: true, title: "Requests register Warehouse", isNotClosable: true},
// ]);

const withTabs =
  ({
    isClosableTabs,
    isMainTabControlled = true,
    label,
    formInitialValues,
    validationSchema,
    validateOnBlur,
    containerStyle,
  }) =>
  (WrappedComponent, additionalTabs = []) => {
    const TabsContainer = props => {
      const [tabs, setTabs] = useState([]);
      const [currentTabIndex, setCurrentTabIndex] = useState(0);
      const [scrollPosition, setScrollPosition] = useState(0);
      const [showScrollButtons, setShowScrollButtons] = useState(false);

      const currentTabIdName = tabs?.[currentTabIndex]?.tabIdName;

      const closeBtnRefs = useRef([]);
      const emptyRef = useRef([]);
      const tabsContainerRef = useRef(null);

      const {t} = useTranslation();

      useEffect(() => {
        setTabs(prev =>
          prev?.length > 0
            ? prev
            : [
                {
                  TabComponent: WrappedComponent,
                  controlled: !!isMainTabControlled,
                  title: label,
                  formInitialValues: formInitialValues,
                  validationSchema,
                  validateOnBlur,
                  ...props,
                },
                ...additionalTabs,
              ],
        );
      }, []);

      useEffect(() => {
        const handleResize = () => {
          if (tabsContainerRef.current) {
            const maxScroll = tabsContainerRef.current.scrollWidth - tabsContainerRef.current.offsetWidth;
            setShowScrollButtons(maxScroll > 0);
          }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        setTabs(prev => prev?.map((tab, i) => ({...tab, index: i})));
        return () => window.removeEventListener("resize", handleResize);
      }, [tabs.length]);

      const addNewTab = newTab => {
        setTabs(prev => [...prev, {...newTab, index: prev.length}]);
      };

      const addTabOnIndex = (newTab, index) => {
        setTabs(prev => [...prev.slice(0, index), {...newTab, index: prev.length}, ...prev.slice(index)]);
      };

      const closeTab = tabIndex => {
        const initialTabs = [...tabs];
        initialTabs.splice(tabIndex, 1);
        setTabs(initialTabs);
        if (tabIndex <= currentTabIndex) {
          setCurrentTabIndex(currentTabIndex - 1);
        }
      };

      const closeByTabIdName = useCallback(
        tabIdName => {
          const initialTabs = tabs.filter(tab => tab?.tabIdName !== tabIdName);
          setTabs(initialTabs);
        },
        [tabs],
      );

      const scrollTabs = direction => {
        const tabWidth = tabsContainerRef.current.querySelector(".tab").offsetWidth;
        const maxScroll = tabsContainerRef.current.scrollWidth - tabsContainerRef.current.offsetWidth;

        let newScrollPosition = scrollPosition + direction * tabWidth + 20;
        newScrollPosition = Math.max(0, Math.min(newScrollPosition, maxScroll));

        tabsContainerRef.current.scrollTo({left: newScrollPosition, behavior: "smooth"});
        setScrollPosition(newScrollPosition);
      };

      const titlesRender = () => {
        return tabs.map((tab, index) => {
          const translatedTitle =
            typeof tab.title === "string" ? (tab.titleKey ? t(tab.titleKey, tab.titleParams) : t(tab.title)) : "";
          const titleText = (tab.headingNumber > 0 && `${tab.headingNumber}. ${translatedTitle}`) || translatedTitle;

          return (
            <div key={tab.tabIdName ?? tab.title} className={"tab " + (index === currentTabIndex ? " selected" : "")}>
              <div title={titleText || undefined} className="container" onClick={() => setCurrentTabIndex(index)}>
                <span className="title">{typeof tab.title === "string" ? titleText : tab.title}</span>
                {!!isClosableTabs && index !== 0 && !tab?.isNotClosable && (
                  <span
                    style={{}}
                    className="tab-close-btn"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (closeBtnRefs.current[index]) {
                        closeBtnRefs.current[index].click();
                      }
                    }}
                  >
                    &#215;
                  </span>
                )}
              </div>
            </div>
          );
        });
      };

      const tabsRender = () => {
        return tabs.map((tab, index) => {
          const {TabComponent, controlled, closable = true, ...tabprops} = tab;
          if (controlled) {
            return (
              <ControlledTab
                key={index}
                closeBtnRefs={closable ? closeBtnRefs : emptyRef}
                tabIndex={index}
                Component={TabComponent}
                {...tabprops}
              />
            );
          } else if (index === currentTabIndex)
            return (
              <div className="page_container fcCol w100" key={index}>
                <div className="overflow_form_modal hmax100 fGrow w100 fcCol aistr jcsb" style={{maxHeight: "100%"}}>
                  <TabComponent key={index} {...tabprops} />
                </div>
              </div>
            );
        });
      };

      const titles = titlesRender();
      const tabsList = tabsRender();

      return (
        <TabsContext.Provider
          value={{
            addNewTab,
            addTabOnIndex,
            tabs,
            setTabs,
            currentTabIndex,
            closeTab,
            setCurrentTabIndex,
            closeByTabIdName,
            currentTabIdName,
          }}
        >
          <div className="limitedSize" style={containerStyle}>
            <div className="tab-container" style={{maxWidth: "100%"}}>
              <div className="tabs-wrapper" ref={tabsContainerRef}>
                <div className="tabs">{titles}</div>
              </div>
              {showScrollButtons && (
                <>
                  <button className="scroll-btn left" onClick={() => scrollTabs(-1)}>
                    &#9664;
                  </button>
                  <button className="scroll-btn right" onClick={() => scrollTabs(1)}>
                    &#9654;
                  </button>
                </>
              )}
            </div>
            {tabs.length > 0 && tabsList}
          </div>
        </TabsContext.Provider>
      );
    };
    return TabsContainer;
  };

export default withTabs;
